import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import {
  Caption,
  FormikTextInput,
  KeyboardSafeAreaScrollView,
  logger,
  Subtitle1,
  Touchable,
  useTheme,
  VectorIcon,
} from "capsule"
import { Formik, useFormikContext } from "formik"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, TouchableOpacity, View } from "react-native"
import { Text } from "react-native-paper"
import * as Yup from "yup"

import SubmitButton from "../../components/SubmitButton"
import { INPUT_HEIGHT } from "../../features/config/Constants"
import { NewClientOnboardingNS } from "../../features/i18n/constants"
import { NewClientOnboardingParamList } from "../../features/Navigation/types"
import { createNewClientDoc } from "../../features/services/NewClientService"

interface FormValues {
  type: "patient" | "professional"
  email: string
  phone: string
  profession: string
}

const NewClientSelectProfileScreen = () => {
  const { t } = useTranslation(NewClientOnboardingNS)
  const navigation =
    useNavigation<StackNavigationProp<NewClientOnboardingParamList, "NewClientSelectProfile">>()

  // Use parent Formik context to get profession from ProfessionalSelectScreen
  const parentFormik = useFormikContext<{ profession?: string }>()

  const {
    colors: {
      surface: { background },
      primary,
    },
    dimensions: { spacing },
  } = useTheme()

  const [selectedType, setSelectedType] = useState<"patient" | "professional">("patient")

  const s = useMemo(
    () => ({
      container: [
        styles.container,
        {
          backgroundColor: background,
        },
      ],
      innerContent: {
        paddingBottom: spacing * 2,
      },
      inputContainer: {
        paddingHorizontal: spacing,
        backgroundColor: background,
      },
      content: {
        gap: spacing * 2,
        paddingHorizontal: spacing * 2,
      },
      radioGroup: [
        styles.radioGroup,
        {
          paddingBottom: spacing,
          gap: spacing,
        },
      ],
      child: { paddingBottom: spacing },
      helperText: {
        paddingBottom: spacing,
        opacity: 0.7,
      },
      input: [
        styles.input,
        {
          paddingLeft: spacing / 4,
        },
      ],
      professionField: [
        styles.professionField,
        {
          height: INPUT_HEIGHT,
          borderBottomWidth: 1,
          borderBottomColor: primary,
          paddingHorizontal: spacing / 4,
          marginBottom: spacing,
        },
      ],
      professionFieldContent: {
        flexDirection: "row" as const,
        justifyContent: "space-between" as const,
        alignItems: "center" as const,
        height: "100%",
      },
      professionPlaceholder: {
        opacity: 0.5,
      },
      radioItem: [
        styles.radioItem,
        {
          borderColor: primary,
          borderRadius: spacing,
          paddingVertical: spacing / 2,
        },
      ],
      radioItemSelected: {
        backgroundColor: `${primary}15`,
        borderWidth: 2,
      },
      radioRow: {
        flexDirection: "column" as const,
        alignItems: "center" as const,
      },
    }),
    [background, spacing, primary],
  )

  const initialValues: FormValues = {
    type: "patient",
    email: "",
    phone: "",
    profession: "",
  }

  // Helper function to get profession label from ID
  const getProfessionLabel = (professionId: string) => {
    switch (professionId) {
      case "kine":
        return t("professionalSelect.kine")
      case "surgeon":
        return t("professionalSelect.surgeon")
      case "gp":
        return t("professionalSelect.gp")
      case "other":
        return t("professionalSelect.other")
      default:
        return ""
    }
  }

  const validationSchema = Yup.object({
    type: Yup.string().oneOf(["patient", "professional"]).required(),
    profession: Yup.string().when("type", {
      is: "professional",
      then: schema => schema.required(t("errors.professionRequired")),
      otherwise: schema => schema.optional(),
    }),
    email: Yup.string()
      .email(t("errors.invalidEmail"))
      .test("email-or-phone", t("errors.emailOrPhoneRequired"), function (value) {
        const { phone } = this.parent
        // At least one of email or phone must be filled
        return !!(value || phone)
      }),
    phone: Yup.string()
      .test("phone-format", t("errors.phoneInvalid"), function (value) {
        // If phone is empty, it's valid (optional)
        if (!value || value.length === 0) {
          return true
        }
        // If phone is provided, it must match the format
        return /^(\+33|0)[1-9](\d{8})$/.test(value)
      })
      .test("email-or-phone", t("errors.emailOrPhoneRequired"), function (value) {
        const { email } = this.parent
        // At least one of email or phone must be filled
        return !!(value || email)
      }),
  })

  const onSubmit = async (values: FormValues) => {
    try {
      // Create new client document with initial data
      await createNewClientDoc({
        type: values.type,
        email: values.email || "Non renseigné",
        phone: values.phone || "Non renseigné",
        ...(values.type === "professional" && { profession: values.profession }),
      })

      if (values.type === "patient") {
        navigation.navigate("NewClientPatientConfirmation")
      } else {
        navigation.navigate("NewClientProfessionalThankYou")
      }
    } catch (error) {
      logger("Error creating new client:", error)
      throw error
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
      validateOnBlur={true}
      validateOnChange={true}
    >
      {({ setFieldValue, values }) => (
        <FormContent
          setFieldValue={setFieldValue}
          values={values}
          parentFormik={parentFormik}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          getProfessionLabel={getProfessionLabel}
          navigation={navigation}
          s={s}
          t={t}
        />
      )}
    </Formik>
  )
}

interface FormContentProps {
  setFieldValue: (field: string, value: any) => void
  values: FormValues
  parentFormik: any
  selectedType: "patient" | "professional"
  setSelectedType: (type: "patient" | "professional") => void
  getProfessionLabel: (professionId: string) => string
  navigation: StackNavigationProp<NewClientOnboardingParamList, "NewClientSelectProfile">
  s: any
  t: any
}

const FormContent: React.FC<FormContentProps> = ({
  setFieldValue,
  values,
  parentFormik,
  selectedType,
  setSelectedType,
  getProfessionLabel,
  navigation,
  s,
  t,
}) => {
  useEffect(() => {
    if (parentFormik.values.profession && parentFormik.values.profession !== values.profession) {
      setFieldValue("profession", parentFormik.values.profession)
    }
  }, [parentFormik.values.profession, values.profession, setFieldValue])

  return (
    <KeyboardSafeAreaScrollView style={s.container} contentContainerStyle={s.innerContent}>
      <View style={s.inputContainer}>
        <View style={s.child}>
          <Subtitle1>{t("selectProfile.instruction")}</Subtitle1>
        </View>

        <View style={s.radioGroup}>
          <TouchableOpacity
            style={[s.radioItem, selectedType === "patient" && s.radioItemSelected]}
            onPress={() => {
              setSelectedType("patient")
              setFieldValue("type", "patient")
              setFieldValue("profession", "")
            }}
            testID="button_patient"
          >
            <View style={s.radioRow}>
              <Text style={styles.bold}>{t("selectProfile.patient")}</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={[s.radioItem, selectedType === "professional" && s.radioItemSelected]}
            onPress={() => {
              setSelectedType("professional")
              setFieldValue("type", "professional")
            }}
            testID="button_professional"
          >
            <View style={s.radioRow}>
              <Text style={styles.bold}>{t("selectProfile.professional1")}</Text>
              <Text style={styles.bold}>{t("selectProfile.professional2")}</Text>
            </View>
          </TouchableOpacity>
        </View>

        {selectedType === "professional" && (
          <Touchable
            onPress={() => navigation.navigate("NewClientProfessionalSelect")}
            style={s.professionField}
            testID="button_profession"
          >
            <View style={s.professionFieldContent}>
              <Subtitle1 style={values.profession ? undefined : s.professionPlaceholder}>
                {values.profession
                  ? getProfessionLabel(values.profession)
                  : t("selectProfile.professionPlaceholder")}
              </Subtitle1>
              <VectorIcon name="chevron-right" category="MaterialIcons" size={24} />
            </View>
          </Touchable>
        )}

        <Caption style={s.helperText}>{t("selectProfile.contactHelper")}</Caption>

        <FormikTextInput
          style={s.input}
          name="email"
          label={t("selectProfile.email")}
          placeholder={t("selectProfile.emailPlaceholder")}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          testID="input_email"
          styles={{ child: s.child }}
        />

        <FormikTextInput
          style={s.input}
          name="phone"
          label={t("selectProfile.phone")}
          placeholder={t("selectProfile.phonePlaceholder")}
          keyboardType="phone-pad"
          styles={{ child: s.child }}
          testID="input_phone"
        />

        <SubmitButton label={t("selectProfile.submit")} />
      </View>
    </KeyboardSafeAreaScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  radioGroup: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  radioItem: {
    flex: 1,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    height: 120,
  },
  input: {
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    borderRadius: 0,
  },
  professionField: {
    justifyContent: "center",
  },
  bold: {
    fontWeight: "bold",
  },
})

export default NewClientSelectProfileScreen
